var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a, _b;
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Teacher } from '@/models/Teacher';
import AsyncSelect from '@/components/async-select/AsyncSelect';
import ErrorHandler from '@/support/ErrorHandler';
import SelectFilter from '@/components/select-filter/SelectFilter';
import { StudentDocumentStatus } from '@/models/StudentDocumentStatus';
import { DocumentableType, StudentDocumentTemplate, StudentDocumentTemplateIncludes } from '@/models/StudentDocumentTemplate';
import { hasRequiredBasicInfo, StudentDocument, StudentDocumentIncludes, } from '@/models/StudentDocument';
import PlanTemplateRow from '@/components/PlanTemplateRow/PlanTemplateRow';
import Draggable from '@/components/Draggable/Draggable';
import MediaComponent from '@/components/Media/Media';
import { TargetType } from '@/models/Media';
import { Lvs } from '@/models/Lvs';
import SelectStudentLevelsDialog from '@/views/Users/Student/Dialogs/CreatePlanDialog/SelectStudentLevelsDialog/SelectStudentLevelsDialog.vue';
import CurriculumSelectDialog from '@/components/CurriculumSelectDialog/CurriculumSelectDialog';
import { Curriculum as CurriculumModel } from '@/models/Curriculum';
import { sortByKey } from '@/support/Object';
import { Student as StudentModel } from '@/models/Student';
import { UserSubject } from '@/models/UserSubject';
import AQuillEditor from '@/components/AQuillEditor/AQuillEditor.vue';
import { generatePlanPayload } from '@/support/GeneratePlanPayload';
import { debounce, sortBy } from 'lodash';
let CreatePlanDialog = class CreatePlanDialog extends Vue {
    constructor() {
        // File structure for Typescript files in your Vue project
        // https://tutorials.atabix.com/frontend/typscript_file_structure/
        super(...arguments);
        // #endregion
        // #region @Refs
        // #endregion
        // #region Class properties
        this.planWithBlocks = null;
        this.planCopy = new StudentDocument();
        this.currentStep = 1;
        this.isLoading = false;
        this.isInitialized = false;
        this.isSaving = false;
        this.isEditingStartDate = false;
        this.isEditingEndDate = false;
        this.isAddingStep = false;
        this.planStatuses = [];
        this.isLoadingPlanStatuses = true;
        this.studentLvs = [];
        this.isLoadingLvs = true;
        this.planTemplates = [];
        this.isLoadingPlanTemplates = true;
        this.isShowingStudentLevelsDialog = false;
        this.isShowingCurriculumSelectDialog = false;
        this.isLoadingUserCurriculum = true;
        this.teacherModel = new Teacher();
        this.selectedTemplate = null;
        this.draggableBlockItems = [];
        this.curriculum = new CurriculumModel();
        this.selectedSubjects = [];
        this.curriculumEditItem = null;
        this.curriculumEditItems = [];
        this.temp = [];
        this.savedDocumentId = undefined;
        this.debouncedAutosave = debounce(this.autosave, 3000, { maxWait: 10000 });
        this.internalSelectedTeachers = [];
        // #endregion
    }
    get isBasicInfoComplete() {
        return hasRequiredBasicInfo(this.planCopy);
    }
    get shownSubjects() {
        return this.selectedSubjects;
    }
    get selectedTeachers() {
        return this.internalSelectedTeachers;
    }
    set selectedTeachers(value) {
        this.internalSelectedTeachers = value.map((teacher) => {
            const oldTeacher = this.internalSelectedTeachers.find((selectedTeacher) => selectedTeacher.value === teacher.value);
            if (!('permission' in teacher)) {
                if (oldTeacher && 'permission' in oldTeacher) {
                    teacher.permission = oldTeacher.permission;
                }
                else {
                    teacher.permission = 'view';
                }
            }
            return teacher;
        });
    }
    /**
     *
     * @param number  refers to the step number, not the index.
     * @returns boolean
     */
    isLastStep(number) {
        if (number < 1) {
            throw new Error('number refers to step number, not array index');
        }
        return this.steps.length === number;
    }
    // #endregion
    // #region Lifecycle Hooks / Init
    async mounted() {
        if (this.plan && this.plan.id) {
            await this.fetchPlanWithBlocks();
            if (this.planCopy.blocks) {
                const plpBlocks = this.planCopy.blocks.filter((block) => block.type === 'plp');
                let fullUserSubjects = [];
                await Promise.all([
                    plpBlocks.map(async (block) => {
                        const blockUserSubjects = block.student_subjects ? block.student_subjects.map((blockUserSubject) => blockUserSubject.subject) : [];
                        fullUserSubjects = await new UserSubject()
                            .filter({ subjects: blockUserSubjects })
                            .include(['level', 'parent', 'period', 'projects', 'children'])
                            .user(this.student)
                            .limit(499)
                            .all();
                        block.student_subjects = fullUserSubjects;
                    }),
                ]);
            }
        }
        await this.initialize();
        // This is stupid but for some reason if put above, it doesn't work. it needs some sort of timeout........
        if (this.plan && this.plan.id) {
            this.goToStep(2);
        }
    }
    async initialize() {
        this.isLoading = true;
        await Promise.all([this.fetchPlanTemplates(), this.fetchPlanStatuses(), this.fetchUserLvs(), this.fetchCurriculum()]);
        if (!this.planCopy.blocks || !this.planCopy.blocks.length) {
            this.planCopy.blocks = [];
            this.appendBlock();
        }
        else {
            this.createPlanBlocksDraggableItems(true);
        }
        this.isLoading = false;
        this.$nextTick(() => {
            if (this.planCopy.teachers && this.planCopy.teachers.length) {
                this.selectedTeachers = this.planCopy.teachers.map((teacher) => ({
                    value: teacher.user.id || '',
                    text: teacher.user.name || '',
                    permission: teacher.permission || 'view',
                }));
                this.teacherSelectInput.setDefaultValue(this.selectedTeachers, false);
            }
            this.$refs.teacherSelect.querySelections('');
            this.isInitialized = true;
        });
    }
    // #endregion
    // #region Class methods
    close() {
        this.reset();
        this.$emit('input', null);
    }
    reset() {
        this.goToStep(1);
        this.isLoading = false;
    }
    cancelDebouncedPayload() {
        this.debouncedAutosave.cancel();
    }
    onShareWithAllTeachersEditableChanged(value) {
        if (value) {
            this.$set(this.planCopy, 'is_visible_for_teachers', false);
            this.clearSelectedTeachers();
        }
    }
    onShareWithAllTeachersVisibleChanged(value) {
        if (value) {
            this.$set(this.planCopy, 'is_editable_for_teachers', false);
            this.clearSelectedTeachers();
        }
    }
    clearSelectedTeachers() {
        const teacherSelect = this.$refs.teacherSelect;
        this.selectedTeachers.forEach((teacher) => teacherSelect?.removeSelected(teacher));
        this.selectedTeachers = [];
    }
    onRemoveTeacher(teacher) {
        this.internalSelectedTeachers = this.internalSelectedTeachers.filter((selectedTeacher) => selectedTeacher.value !== teacher.value);
        this.$refs.teacherSelect.removeSelected(teacher);
    }
    goToStep(number) {
        if (number < 0) {
            return;
        }
        this.currentStep = number;
    }
    toNextStep() {
        this.goToStep(this.currentStep + 1);
    }
    onStepClick(number) {
        if (number > 1 && !this.isBasicInfoComplete) {
            return;
        }
        this.goToStep(number);
    }
    toPrevStep() {
        this.goToStep(this.currentStep - 1);
    }
    openStudentLevelsDialog() {
        this.isShowingStudentLevelsDialog = true;
    }
    closeStudentLevelsDialog() {
        this.isShowingStudentLevelsDialog = false;
    }
    openCurriculumSelectDialog() {
        this.isShowingCurriculumSelectDialog = true;
    }
    closeCurriculumSelectDialog(subjects) {
        if (subjects && subjects.length) {
            this.addSubjects(subjects);
        }
        this.isShowingCurriculumSelectDialog = false;
    }
    onClickEditExistingCurriculum(item, subjects) {
        this.isShowingCurriculumSelectDialog = true;
        this.curriculumEditItem = item;
        this.curriculumEditItems = subjects;
    }
    editExistingCurriculum(emit) {
        !emit.items.length ? this.onRemoveObjective(emit.item) : this.editDragItem(emit);
        this.isShowingCurriculumSelectDialog = false;
    }
    editDragItem(emit) {
        const item = this.draggableBlockItems.find((item) => item.id === emit.item.id);
        if (!item) {
            return;
        }
        item.block.student_subjects = emit.items;
    }
    addSubjects(subjects) {
        this.appendBlock({
            title: '',
            content: '',
            id: '',
            sort: this.draggableBlockItems.length - 1,
            type: 'plp',
            student_subjects: subjects,
        });
    }
    onClickAddStudentLvs(lvs) {
        this.appendBlock({
            title: lvs.name,
            content: lvs.lvs_note,
            id: lvs.id,
            type: 'lvs',
            sort: this.draggableBlockItems.length - 1,
        });
    }
    onClickAddObjective(lvs) {
        this.appendBlock({
            title: lvs.name,
            content: lvs.lvs_note,
            id: lvs.id,
            type: 'text',
            sort: this.draggableBlockItems.length - 1,
        });
    }
    isArray(item) {
        return Array.isArray(item);
    }
    isPlp(draggableStudentDocumentBlock) {
        return draggableStudentDocumentBlock.block.type === 'plp';
    }
    onRemoveObjective(draggableItem) {
        this.openConfirmDeleteDialog(draggableItem);
    }
    openConfirmDeleteDialog(draggableItem) {
        this.$store.dispatch('openDialog', this.removeObjectiveDialogOptions(draggableItem));
    }
    removeObjectiveDialogOptions(draggableItem) {
        return {
            title: this.$t('dialogOptions.confirmation').toString(),
            text: this.$tc('scoollinks.dialog.delete.text').toString(),
            type: 'warning',
            buttons: {
                confirm: {
                    text: this.$t('dialogOptions.button.delete').toString(),
                    action: () => {
                        this.removeObjective(draggableItem);
                    },
                },
                cancel: {
                    text: this.$t('dialogOptions.button.cancel').toString(),
                },
            },
        };
    }
    removeObjective(draggableItem) {
        this.isAddingStep = true;
        if (!this.planCopy.blocks) {
            return;
        }
        const itemIndex = this.planCopy.blocks.findIndex((item) => draggableItem.block.id === item.id);
        if (this.draggableBlockItems) {
            this.draggableBlockItems = this.draggableBlockItems.filter((item) => item.id !== draggableItem.id);
        }
        if (itemIndex > -1) {
            this.planCopy.blocks.splice(itemIndex, 1);
        }
        this.isAddingStep = false;
    }
    getEmptyStudentBlock(type = 'text') {
        return {
            content: '',
            title: type === 'simple-text' ? '' : '',
            id: '',
            sort: 0,
            type,
        };
    }
    appendBlockByType(type = 'text') {
        const newBlock = this.getEmptyStudentBlock(type);
        this.appendBlock(newBlock);
    }
    appendBlock(block, isExistingBlock = false) {
        this.isAddingStep = true;
        if (!this.planCopy.blocks) {
            this.planCopy.blocks = [];
        }
        const newBlock = block || this.getEmptyStudentBlock();
        const draggableStudentDocumentBlock = this.transformToDraggableItem(newBlock);
        this.draggableBlockItems.push(draggableStudentDocumentBlock);
        if (!isExistingBlock) {
            this.planCopy.blocks.push(newBlock);
        }
        this.isAddingStep = false;
    }
    transformToDraggableItem(block) {
        return {
            id: Math.random()
                .toString(36)
                .replace(/[^a-z]+/g, '')
                .substr(0, 5),
            block,
        };
    }
    onReOrder(event) {
        this.draggableBlockItems = sortBy(this.draggableBlockItems, (item) => event.items.indexOf(item.id));
        this.planCopy.blocks = this.draggableBlockItems.map((item) => item.block);
    }
    fillTemplates() {
        const foundTemplates = this.planTemplates.find((template) => template.id === (this.selectedTemplate && this.selectedTemplate.id));
        // foundTemplates.forEach((template) => this.createPlanBlocksDraggableItems(template));
        if (foundTemplates && foundTemplates.blocks) {
            this.draggableBlockItems = [];
            this.planCopy.blocks = [];
            foundTemplates.blocks.forEach((template) => this.appendBlock(template));
            // this.planCopy.blocks = [...foundTemplates.blocks];
            // this.createPlanBlocksDraggableItems();
        }
    }
    onSubmit() {
        const isPublish = true;
        this.createPlan(isPublish);
    }
    generatePlanCreatePayload(isPublish = false) {
        return generatePlanPayload({
            plan: this.planCopy,
            entityId: this.$route.params.id || '',
            entityFieldName: 'user_id',
            documentableType: DocumentableType.USER,
            blocks: this.formatBlocksPayload(),
            selectedTeachers: this.selectedTeachers,
            isPublish,
            id: this.savedDocumentId,
        });
    }
    formatBlocksPayload() {
        const blocksPayload = [];
        if (this.planCopy.blocks) {
            this.planCopy.blocks.forEach((block) => {
                if (block.type === 'plp') {
                    blocksPayload.push({
                        type: block.type,
                        student_subjects: block.student_subjects ? block.student_subjects.map((block) => (block.id ? block.id : '')) : [],
                        sort: block.sort,
                    });
                }
                else {
                    blocksPayload.push({
                        title: block.title,
                        content: block.content,
                        sort: block.sort,
                        type: block.type,
                    });
                }
            });
        }
        return blocksPayload;
    }
    createPlanBlocksDraggableItems(isExistingBlock = false) {
        if (this.planCopy && this.planCopy.blocks && this.planCopy.blocks.length) {
            this.planCopy.blocks.forEach((block) => this.appendBlock(block, isExistingBlock));
        }
    }
    // #endregion
    // #region Async methods
    async fetchCurriculum() {
        this.curriculum
            .current()
            .include(['children', 'subjects'])
            .first()
            .then((curriculum) => {
            this.curriculum = curriculum;
            const areas = this.curriculum.subjects || [];
            areas.forEach((area, index) => {
                this.curriculum.subjects[index] = this.curriculum.dataToModel(area, 'Subject');
                const topics = this.curriculum.subjects[index].children || [];
                topics.forEach((topic, i) => {
                    this.curriculum.subjects[index].children[i] = this.curriculum.dataToModel(topic, 'Subject');
                });
                topics.sort(sortByKey('name'));
                this.$set(this.curriculum.subjects[index], 'children', topics);
            });
            areas.sort(sortByKey('name'));
            this.$set(this.curriculum.subjects, 'subjects', areas);
            this.isLoadingUserCurriculum = false;
        });
    }
    async fetchPlanWithBlocks() {
        if (!this.plan) {
            return;
        }
        this.planWithBlocks = await new StudentDocument().include([StudentDocumentIncludes.STATUS, StudentDocumentIncludes.BLOCKS, StudentDocumentIncludes.STUDENT_SUBJECTS, StudentDocumentIncludes.TEACHERS, StudentDocumentIncludes.CREATED_BY, 'parent']).find(this.plan.id);
        if (this.planWithBlocks) {
            this.planCopy = this.planWithBlocks.clone();
            this.savedDocumentId = this.planWithBlocks.id;
        }
    }
    async autosave() {
        if (!this.isInitialized || this.isSaving)
            return;
        await this.savePlan(false);
    }
    async createPlan(isPublish = false) {
        this.cancelDebouncedPayload();
        await this.savePlan(isPublish);
    }
    async savePlan(isPublish) {
        this.isSaving = true;
        if (isPublish) {
            this.isLoading = true;
        }
        try {
            const plan = await new StudentDocument().create(this.generatePlanCreatePayload(isPublish));
            if (!plan)
                return;
            this.savedDocumentId = plan.id || this.savedDocumentId;
            if (isPublish) {
                this.$emit('created');
                this.close();
                return;
            }
            this.$emit('updated');
        }
        catch (error) {
            ErrorHandler.network(error);
        }
        finally {
            this.isSaving = false;
            if (isPublish) {
                this.isLoading = false;
            }
        }
    }
    async fetchPlanStatuses() {
        this.isLoadingPlanStatuses = true;
        this.planStatuses = await new StudentDocumentStatus().all();
        this.isLoadingPlanStatuses = false;
    }
    async fetchPlanTemplates() {
        this.isLoadingPlanTemplates = true;
        this.planTemplates = await new StudentDocumentTemplate().include([StudentDocumentTemplateIncludes.BLOCKS]).all();
        this.isLoadingPlanTemplates = false;
    }
    async fetchUserLvs() {
        this.isLoadingLvs = true;
        new Lvs()
            .filter({ users: [this.$route.params.id] })
            .all()
            .then((result) => {
            if (Array.isArray(result)) {
                this.studentLvs = result.map((area) => {
                    return {
                        id: area.id,
                        name: area.name,
                        lvs_note: area.users[this.$route.params.id].notes,
                    };
                });
            }
            this.isLoadingLvs = false;
        })
            .catch((error) => {
            ErrorHandler.network(error);
            this.isLoadingLvs = false;
        });
    }
    // #endregion
    // #region Getters & Setters
    get canShowDeleteIcon() {
        return !!(this.draggableBlockItems && this.draggableBlockItems.length > 1);
    }
    get dialogType() {
        return this.planCopy.id ? DialogTypeEnum.UPDATE : DialogTypeEnum.CREATE;
    }
    get teacherSelectInput() {
        return this.$refs.teacherSelect;
    }
    get mediaFilters() {
        return {
            target: {
                id: (this.plan && this.plan.id) || '',
                type: TargetType.DOCUMENT,
            },
        };
    }
    get required() {
        return (value) => {
            return !!value || 'Field is required';
        };
    }
    get steps() {
        return [
            {
                title: this.$tc('student_plan.dialog.steps.one'),
                visible: true,
                completed: () => this.currentStep > 1,
            },
            {
                title: this.$tc('student_plan.dialog.steps.two'),
                visible: true,
                completed: () => this.currentStep > 2,
            },
            {
                title: this.$tc('student_plan.dialog.steps.three'),
                visible: !!this.plan,
                completed: () => this.currentStep > 3,
            },
        ];
    }
    get teacherViewOptions() {
        return [
            { name: this.$tc('View and edit'), id: 'edit' },
            { name: this.$tc('View'), id: 'view' },
        ];
    }
    // #endregion
    // #region @Watchers
    onPlanCopyChanged() {
        if (!this.isInitialized || this.isSaving)
            return;
        this.debouncedAutosave();
    }
    onSelectedTeachersChanged() {
        if (!this.isInitialized || this.isSaving)
            return;
        this.debouncedAutosave();
    }
};
__decorate([
    Prop({ default: null }),
    __metadata("design:type", Object)
], CreatePlanDialog.prototype, "plan", void 0);
__decorate([
    Prop({ required: true }),
    __metadata("design:type", typeof (_b = typeof StudentModel !== "undefined" && StudentModel) === "function" ? _b : Object)
], CreatePlanDialog.prototype, "student", void 0);
__decorate([
    Watch('planCopy', { deep: true }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], CreatePlanDialog.prototype, "onPlanCopyChanged", null);
__decorate([
    Watch('internalSelectedTeachers', { deep: true }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], CreatePlanDialog.prototype, "onSelectedTeachersChanged", null);
CreatePlanDialog = __decorate([
    Component({
        components: {
            SelectFilter,
            PlanTemplateRow,
            AsyncSelect,
            AQuillEditor,
            Draggable,
            MediaComponent,
            SelectStudentLevelsDialog,
            CurriculumSelectDialog,
        },
    })
], CreatePlanDialog);
export default CreatePlanDialog;
var DialogTypeEnum;
(function (DialogTypeEnum) {
    DialogTypeEnum[DialogTypeEnum["CREATE"] = 1] = "CREATE";
    DialogTypeEnum[DialogTypeEnum["UPDATE"] = 2] = "UPDATE";
})(DialogTypeEnum || (DialogTypeEnum = {}));
